/* ══════════════════════════════════════════════════════════════
   LUXIRE SUPPORT HUB — GrooveHQ Integration
   ══════════════════════════════════════════════════════════════
   Uses LuxireStorage.api(method, path, body) which routes through
   /apps/luxire-api/ on the Railway proxy.

   ENDPOINTS (Railway proxy must implement):
   ──────────────────────────────────────────────────────────────
   GET  /support/tickets?email={email}&page={n}&per_page={n}&state={open|pending|closed}
     → { tickets: [...], meta: { total, page, per_page, pages } }

   GET  /support/tickets/:id
     → { ticket: { id, subject, state, category, order_number, created_at, updated_at },
         messages: [{ id, author, author_name, body, created_at, attachments: [{ id, url, filename, content_type, size }] }] }

   POST /support/tickets
     Body: { email, name, subject, body, category, order_number, attachment_ids: [] }
     → { ticket: { id, ... } }

   POST /support/tickets/:id/reply
     Body: { body, attachment_ids: [] }
     → { message: { id, ... } }

   POST /support/attachments  (FormData, not JSON)
     Body: FormData with file field
     → { attachment: { id, url, filename, content_type, size } }
   ══════════════════════════════════════════════════════════════ */

window.SupportHub = (function() {
  'use strict';

  /* ── Configuration ── */
  const CONFIG = {
    perPage: 10,
    maxAttachments: 5,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'application/pdf'],
  };

  /* ── State ── */
  let state = {
    tickets: [],
    currentFilter: 'all',
    searchQuery: '',
    currentPage: 1,
    totalPages: 1,
    currentTicketId: null,
    currentTicket: null,
    replyAttachments: [],   // { id, url, filename, file? }
    createAttachments: [],  // { id, url, filename, file? }
    loading: false,
    // Tickets the user JUST created in this session that the backend list-endpoint
    // doesn't yet return (eventual consistency / known backend bug). We keep them
    // here and merge into state.tickets on every refresh so the user keeps seeing
    // their work. Cleared on page reload.
    _optimisticTickets: [],
  };

  /* ── DOM helpers ── */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  // Escapes for HTML text AND attribute values (quotes included), so nothing taken from a
  // URL or a reply can close an attribute it is written into.
  function esc(s) {
    if (!s) return '';
    const el = document.createElement('span');
    el.textContent = s;
    return el.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatDate(str) {
    if (!str) return '';
    const d = new Date(str);
    const now = new Date();
    const diff = now - d;
    // Under 1 hour
    if (diff < 3600000) return Math.max(1, Math.floor(diff / 60000)) + 'm ago';
    // Under 24 hours
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    // Under 7 days
    if (diff < 604800000) return Math.floor(diff / 86400000) + 'd ago';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function formatDateFull(str) {
    if (!str) return '';
    const d = new Date(str);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  /* ── API Client ── */
  // Uses LuxireStorage.api() which routes through /apps/luxire-api/
  // File uploads use direct fetch since LuxireStorage.api is JSON-only
  const api = {
    async get(path) {
      return LuxireStorage.api('GET', path);
    },

    async post(path, body) {
      return LuxireStorage.api('POST', path, body);
    },

    async upload(file) {
      const PROXY_BASE = '/apps/luxire-api';
      const form = new FormData();
      form.append('file', file);
      const r = await fetch(PROXY_BASE + '/support/attachments', {
        method: 'POST',
        body: form,
      });
      if (!r.ok) throw new Error(`Upload error ${r.status}`);
      return r.json();
    },
  };

  /* ══════════════════════════════════════════
     TICKET LIST
  ══════════════════════════════════════════ */

  async function loadTickets(page = 1, append = false, silent = false) {
    const listEl = $('#sh-ticket-list');
    const emptyEl = $('#sh-empty');
    const errorEl = $('#sh-error');
    const loadMoreEl = $('#sh-load-more');
    if (!listEl) return;

    // Skip the loading-spinner flash for silent refreshes (e.g. coming back from a thread,
    // or refreshing right after ticket creation). The previous list stays visible until
    // the new data arrives.
    if (!append && !silent) listEl.innerHTML = '<div class="ah-loading"><div class="ah-spinner"></div> Loading tickets…</div>';
    if (emptyEl) emptyEl.style.display = 'none';
    if (errorEl) errorEl.style.display = 'none';
    if (loadMoreEl) loadMoreEl.style.display = 'none';

    const email = window.__luxire_customer_email;
    if (!email) {
      showError('No customer email found. Please log in.');
      return;
    }

    try {
      let url = `/support/tickets?email=${encodeURIComponent(email)}&page=${page}&per_page=${CONFIG.perPage}`;
      if (state.currentFilter !== 'all') url += `&state=${state.currentFilter}`;

      const data = await api.get(url);
      const tickets = data.tickets || [];
      const meta = data.meta || {};

      // Diagnostic: log what the backend returned so we can debug "tickets vanished" reports
      console.log('[SupportHub] loaded', tickets.length, 'ticket(s) for', email, '— meta:', meta);

      state.currentPage = meta.page || page;
      state.totalPages = meta.pages || 1;

      if (append) {
        state.tickets = state.tickets.concat(tickets);
      } else {
        state.tickets = tickets;
      }

      // Merge in any optimistic (just-created-in-this-session) tickets the backend hasn't
      // echoed back yet. If the backend ever does return them, we DON'T duplicate — the
      // existing entry wins. Once the customer reloads the page, optimistic state is cleared.
      if (state._optimisticTickets && state._optimisticTickets.length) {
        const backendIds = new Set(state.tickets.map(t => String(t.id || t.number)));
        const missingFromBackend = state._optimisticTickets.filter(
          opt => !backendIds.has(String(opt.id || opt.number))
        );
        if (missingFromBackend.length) {
          console.warn(
            '[SupportHub] Backend list endpoint is missing',
            missingFromBackend.length,
            'just-created ticket(s) — keeping them visible client-side. This is a backend issue to fix server-side.'
          );
          state.tickets = missingFromBackend.concat(state.tickets);
        } else {
          // Backend has caught up — we can drop optimistic state
          state._optimisticTickets = [];
        }
      }

      if (state.tickets.length === 0) {
        listEl.innerHTML = '';
        if (emptyEl) emptyEl.style.display = '';
        updateUnreadBadge();  // hides the sidebar badge when zero
        return;
      }

      renderTicketList();

      if (state.currentPage < state.totalPages && loadMoreEl) {
        loadMoreEl.style.display = '';
      }

      updateUnreadBadge();
    } catch (err) {
      console.error('[SupportHub] Failed to load tickets for', email, '—', err);
      if (!append) listEl.innerHTML = '';
      showError();
    }
  }

  function renderTicketList() {
    const listEl = $('#sh-ticket-list');
    const emptyEl = $('#sh-empty');
    const errorEl = $('#sh-error');
    if (!listEl) return;

    // Hide stale empty/error states first — we'll re-show #sh-empty below if appropriate
    if (emptyEl) emptyEl.style.display = 'none';
    if (errorEl) errorEl.style.display = 'none';

    // No tickets at all (regardless of search) → show the real empty state, not the filter-mismatch one
    if (state.tickets.length === 0) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = '';
      return;
    }

    // Apply search/filter to a copy of state.tickets
    let tickets = state.tickets;
    if (state.searchQuery) {
      const q = state.searchQuery.toLowerCase();
      tickets = tickets.filter(t =>
        (t.title || '').toLowerCase().includes(q) ||
        (t.summary || '').toLowerCase().includes(q) ||
        (String(t.number) || '').includes(q)
      );
    }

    if (tickets.length === 0) {
      // Tickets exist but were filtered out — show the filter-mismatch hint, NOT the empty state
      listEl.innerHTML = `
        <div class="sh-no-results">
          <p>No tickets match your ${state.searchQuery ? 'search' : 'filter'}.</p>
        </div>`;
      return;
    }

    listEl.innerHTML = tickets.map(t => {
      const statusMap = {
        open: { icon: '<span class="sh-dot sh-dot--open"></span>', label: 'Open', cls: 'open' },
        opened: { icon: '<span class="sh-dot sh-dot--open"></span>', label: 'Open', cls: 'open' },
        pending: { icon: '<span class="sh-dot sh-dot--pending"></span>', label: 'Awaiting Reply', cls: 'pending' },
        closed: { icon: '<span class="sh-dot sh-dot--closed"></span>', label: 'Resolved', cls: 'closed' },
        archived: { icon: '<span class="sh-dot sh-dot--archived"></span>', label: 'Closed', cls: 'archived' },
      };
      const s = statusMap[t.state] || statusMap.open;
      const unread = t._unread ? ' sh-ticket--unread' : '';
      const ticketId = t.number || t.id || '';
      const tags = (t.tags || []).map(tag => `<span class="sh-ticket__cat">${esc(tag)}</span>`).join('');

      return `
        <div class="sh-ticket${unread}" data-ticket-id="${esc(ticketId)}" data-status="${s.cls}">
          <div class="sh-ticket__status">
            <span class="sh-ticket__status-icon">${s.icon}</span>
          </div>
          <div class="sh-ticket__body">
            <div class="sh-ticket__subject">${esc(t.title || t.subject || 'Conversation')}</div>
            <div class="sh-ticket__meta">
              ${tags}
              <span class="sh-ticket__date">Updated ${formatDate(t.updatedAt || t.updated_at || t.createdAt || t.created_at)}</span>
            </div>
            <div class="sh-ticket__preview">${esc(t.summary || '')}</div>
          </div>
          <div class="sh-ticket__badge sh-ticket__badge--${s.cls}">${s.label}</div>
        </div>`;
    }).join('');

    // Bind click handlers
    $$('.sh-ticket', listEl).forEach(el => {
      el.addEventListener('click', () => openThread(el.dataset.ticketId));
    });
  }

  function formatCategory(cat) {
    const map = {
      fit_issue: 'Fit Issue',
      fabric_question: 'Fabric Question',
      order_status: 'Order Status',
      alteration: 'Alteration',
      customization: 'Customization',
      returns: 'Returns',
      general: 'General',
    };
    return map[cat] || cat;
  }

  function showError(msg) {
    const errorEl = $('#sh-error');
    if (!errorEl) return;
    if (msg) errorEl.querySelector('p').innerHTML = msg;
    errorEl.style.display = '';
  }

  function updateUnreadBadge() {
    const unreadCount = state.tickets.filter(t => t._unread).length;
    // Update every badge instance — both the legacy .sh-unread-badge (if it exists)
    // AND the sidebar nav badge (which now also carries the .sh-unread-badge class).
    const badges = $$('.sh-unread-badge, .lx-sidebar__badge');
    badges.forEach(badge => {
      if (unreadCount > 0) {
        badge.textContent = unreadCount;
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    });
    // Stash count on window so external callers (e.g., other tabs/sections) can read it
    window.__luxire_unread_tickets_count = unreadCount;
  }

  /* ══════════════════════════════════════════
     THREAD VIEW
  ══════════════════════════════════════════ */

  async function openThread(ticketId) {
    showView('thread');
    state.currentTicketId = ticketId;
    state.replyAttachments = [];

    const headerEl = $('#sh-thread-header');
    const msgsEl = $('#sh-thread-messages');
    const replyText = $('#sh-reply-text');

    if (headerEl) headerEl.innerHTML = '<div class="ah-loading"><div class="ah-spinner"></div> Loading…</div>';
    if (msgsEl) msgsEl.innerHTML = '';
    if (replyText) replyText.value = '';
    clearAttachmentPreviews('reply');
    updateSendButton('reply');

    try {
      const data = await api.get(`/support/tickets/${ticketId}`);
      const ticket = data.ticket || {};
      const messages = data.messages || [];
      state.currentTicket = ticket;

      renderThreadHeader(ticket);
      renderThreadMessages(messages);
    } catch (err) {
      console.error('SupportHub: Failed to load thread', err);
      if (headerEl) {
        // No inline handler: the ticket id never becomes part of script text.
        headerEl.innerHTML = `
        <div class="sh-thread-error">
          <p>Unable to load this conversation. <button type="button" class="sh-link" data-sh-thread-retry>Try again</button></p>
        </div>`;
        const retryBtn = headerEl.querySelector('[data-sh-thread-retry]');
        if (retryBtn) retryBtn.addEventListener('click', () => openThread(ticketId));
      }
    }
  }

  // File icon from the shared icon set (assets/lx-icons.js); empty if that script is missing.
  function fileIcon(size) { return (window.LxIcon && window.LxIcon('file', size)) || ''; }

  function renderThreadHeader(ticket) {
    const el = $('#sh-thread-header');
    if (!el) return;

    const statusMap = {
      open: { icon: '<span class="sh-dot sh-dot--open"></span>', label: 'Open', cls: 'open' },
      opened: { icon: '<span class="sh-dot sh-dot--open"></span>', label: 'Open', cls: 'open' },
      pending: { icon: '<span class="sh-dot sh-dot--pending"></span>', label: 'Awaiting Reply', cls: 'pending' },
      closed: { icon: '<span class="sh-dot sh-dot--closed"></span>', label: 'Resolved', cls: 'closed' },
      archived: { icon: '<span class="sh-dot sh-dot--archived"></span>', label: 'Closed', cls: 'archived' },
    };
    const s = statusMap[ticket.state] || statusMap.open;

    el.innerHTML = `
      <div class="sh-thread-info">
        <h2 class="sh-thread-info__subject">${esc(ticket.title || ticket.subject || 'Conversation')}</h2>
        <div class="sh-thread-info__meta">
          <span class="sh-ticket__badge sh-ticket__badge--${s.cls}">${s.icon} ${s.label}</span>
          ${(ticket.tags || []).map(tag => `<span class="sh-ticket__cat">${esc(tag)}</span>`).join('')}
          <span class="sh-thread-info__date">Created ${formatDateFull(ticket.createdAt || ticket.created_at)}</span>
        </div>
      </div>`;
  }

  // Decide whether a message came from the logged-in customer or a support
  // agent. Backends vary in how they expose this:
  //   - Some set `author: "customer"` / `"agent"` explicitly.
  //   - Some only expose sender email/name and expect us to infer.
  //   - Groove conversations sometimes return raw inbound-email metadata.
  // This helper checks the explicit flag first, then falls back to email
  // comparison against the logged-in customer, then a name-heuristic.
  function isCustomerMessage(m) {
    // 1. Explicit author flag — preferred path
    var author = String(m.author || m.author_type || m.authorType || m.sender_type || m.senderType || '').toLowerCase();
    if (author === 'customer' || author === 'user' || author === 'end-user' || author === 'enduser') return true;
    if (author === 'agent' || author === 'staff' || author === 'admin' || author === 'team') return false;

    // 2. Email match against the logged-in customer
    var custEmail = String(window.__luxire_customer_email || '').toLowerCase().trim();
    if (custEmail) {
      var senderEmail = String(
        m.author_email || m.authorEmail ||
        m.from_email || m.fromEmail ||
        m.sender_email || m.senderEmail ||
        m.from || ''
      ).toLowerCase().trim();
      // Some APIs put "Name <email@x.com>" in `from` — extract the email.
      var match = senderEmail.match(/<([^>]+)>/);
      if (match) senderEmail = match[1].trim();
      if (senderEmail) return senderEmail === custEmail;
    }

    // 3. Name heuristic — anything containing "luxire" or "team" or "support" is an agent
    var name = String(m.author_name || m.authorName || m.from_name || m.fromName || '').toLowerCase();
    if (name.indexOf('luxire') >= 0 || name.indexOf('team') >= 0 || name.indexOf('support') >= 0) return false;

    // 4. Default to agent if we genuinely can't tell — agent label is the safer
    // mistake than mislabeling an agent message as "You" (which would be confusing).
    return false;
  }

  function renderThreadMessages(messages) {
    const el = $('#sh-thread-messages');
    if (!el) return;

    el.innerHTML = messages.map(m => {
      const isCustomer = isCustomerMessage(m);
      const attachments = (m.attachments || []).map(a => renderAttachment(a)).join('');

      return `
        <div class="sh-msg sh-msg--${isCustomer ? 'customer' : 'agent'}">
          <div class="sh-msg__header">
            <span class="sh-msg__author sh-msg__author--${isCustomer ? 'customer' : 'agent'}">
              ${isCustomer ? 'You' : esc(m.authorName || m.author_name || m.from_name || m.fromName || 'Luxire Team')}
            </span>
            <span class="sh-msg__time">${formatDateFull(m.createdAt || m.created_at)}</span>
          </div>
          <div class="sh-msg__body">${sanitizeHTML(m.body || '')}</div>
          ${attachments ? `<div class="sh-msg__attachments">${attachments}</div>` : ''}
        </div>`;
    }).join('');

    // Scroll to bottom
    el.scrollTop = el.scrollHeight;

    // Bind lightbox handlers
    $$('.sh-attachment--image', el).forEach(img => {
      img.addEventListener('click', () => openLightbox(img.dataset.url));
    });
  }

  function renderAttachment(a) {
    const type = a.contentType || a.content_type || '';
    const name = a.fileName || a.filename || 'file';
    const isImage = type.startsWith('image/');
    if (isImage) {
      return `
        <div class="sh-attachment sh-attachment--image" data-url="${esc(a.url)}">
          <img src="${esc(a.url)}" alt="${esc(name)}" loading="lazy">
          <span class="sh-attachment__name">${esc(name)}</span>
        </div>`;
    }
    const sizeStr = a.size ? ` (${formatFileSize(a.size)})` : '';
    return `
      <a href="${esc(a.url)}" target="_blank" class="sh-attachment sh-attachment--file" rel="noopener">
        <span class="sh-attachment__icon">${fileIcon(16)}</span>
        <span class="sh-attachment__name">${esc(name)}${sizeStr}</span>
      </a>`;
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  function sanitizeHTML(html) {
    // Allow basic formatting, strip scripts and dangerous tags
    const div = document.createElement('div');
    div.innerHTML = html;
    // Remove script tags
    div.querySelectorAll('script,style,iframe,object,embed,form').forEach(el => el.remove());
    // Remove event handlers
    div.querySelectorAll('*').forEach(el => {
      [...el.attributes].forEach(attr => {
        if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
      });
    });
    return div.innerHTML;
  }

  /* ── Reply ── */

  async function sendReply() {
    const textEl = $('#sh-reply-text');
    const body = (textEl ? textEl.value : '').trim();
    if (!body && state.replyAttachments.length === 0) return;

    const sendBtn = $('#sh-reply-send-btn');
    if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Sending…'; }

    try {
      // Upload any pending files first
      const attachmentIds = await uploadPendingFiles(state.replyAttachments);

      await api.post(`/support/tickets/${state.currentTicketId}/reply`, {
        body: body,
        attachment_ids: attachmentIds,
      });

      // Re-load thread to show the new message
      state.replyAttachments = [];
      if (textEl) textEl.value = '';
      clearAttachmentPreviews('reply');
      await openThread(state.currentTicketId);

      // Update ticket in list (it's now open)
      const ticket = state.tickets.find(t => (t.number || t.id) == state.currentTicketId);
      if (ticket) {
        ticket.updatedAt = new Date().toISOString();
        ticket.state = 'open';
      }
    } catch (err) {
      console.error('SupportHub: Failed to send reply', err);
      alert('Failed to send reply. Please try again.');
    } finally {
      if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = 'Send Reply'; }
      updateSendButton('reply');
    }
  }

  /* ══════════════════════════════════════════
     NEW TICKET
  ══════════════════════════════════════════ */

  function openCreateView() {
    showView('create');
    state.createAttachments = [];
    // Reset form
    const fields = ['sh-category', 'sh-order', 'sh-subject', 'sh-message'];
    fields.forEach(id => { const el = $('#' + id); if (el) el.value = ''; });
    clearAttachmentPreviews('create');
    updateSubmitButton();
  }

  async function submitTicket() {
    const category = ($('#sh-category') || {}).value;
    const orderNumber = ($('#sh-order') || {}).value;
    const subject = ($('#sh-subject') || {}).value.trim();
    const message = ($('#sh-message') || {}).value.trim();

    if (!subject || !message) {
      alert('Please provide a subject and message.');
      return;
    }

    const submitBtn = $('#sh-submit-ticket-btn');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Submitting…'; }

    try {
      // Upload attachments
      const attachmentIds = await uploadPendingFiles(state.createAttachments);

      const data = await api.post('/support/tickets', {
        email: window.__luxire_customer_email,
        name: window.__luxire_customer_name || '',
        subject: subject,
        body: message,
        category: category,
        order_number: orderNumber,
        attachment_ids: attachmentIds,
      });

      // Go to the new ticket's thread
      if (data.ticket && data.ticket.id) {
        // Optimistically remember this ticket so it shows in the list immediately
        // and survives subsequent silent refreshes (backend list endpoint has shown
        // eventual-consistency lag — sometimes never echoes new tickets back).
        state._optimisticTickets.unshift(data.ticket);
        // Refresh state.tickets in the background so when the user clicks
        // "Back to Tickets", the new ticket is already in the list.
        loadTickets(1, false, true);
        await openThread(data.ticket.id);
      } else {
        showView('list');
        await loadTickets();
      }
    } catch (err) {
      console.error('SupportHub: Failed to create ticket', err);
      alert('Failed to submit ticket. Please try again.');
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Submit Ticket'; }
    }
  }

  /* ══════════════════════════════════════════
     FILE UPLOADS
  ══════════════════════════════════════════ */

  function handleFileSelect(files, context) {
    // context: 'reply' or 'create'
    const attachments = context === 'reply' ? state.replyAttachments : state.createAttachments;

    for (const file of files) {
      if (attachments.length >= CONFIG.maxAttachments) {
        alert(`Maximum ${CONFIG.maxAttachments} files allowed.`);
        break;
      }
      if (file.size > CONFIG.maxFileSize) {
        alert(`${file.name} is too large (max ${CONFIG.maxFileSize / 1048576}MB).`);
        continue;
      }
      if (!CONFIG.allowedTypes.includes(file.type) && !file.name.match(/\.heic$/i)) {
        alert(`${file.name}: Unsupported file type. Use JPG, PNG, HEIC, or PDF.`);
        continue;
      }

      // Create preview entry (not yet uploaded)
      const entry = {
        id: null,
        filename: file.name,
        file: file,
        url: null,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      };
      attachments.push(entry);
    }

    renderAttachmentPreviews(context);
    if (context === 'reply') updateSendButton('reply');
    if (context === 'create') updateSubmitButton();
  }

  async function uploadPendingFiles(attachments) {
    const ids = [];
    for (const a of attachments) {
      if (a.id) {
        ids.push(a.id);
        continue;
      }
      if (!a.file) continue;
      try {
        const data = await api.upload(a.file);
        a.id = data.attachment.id;
        a.url = data.attachment.url;
        ids.push(a.id);
      } catch (err) {
        console.error('Upload failed for', a.filename, err);
        throw err;
      }
    }
    return ids;
  }

  function renderAttachmentPreviews(context) {
    const attachments = context === 'reply' ? state.replyAttachments : state.createAttachments;
    const containerId = context === 'reply' ? 'sh-reply-attachments' : 'sh-create-previews';
    const container = $('#' + containerId);
    if (!container) return;

    if (attachments.length === 0) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = attachments.map((a, i) => {
      const thumb = a.previewUrl
        ? `<img src="${a.previewUrl}" alt="${esc(a.filename)}">`
        : `<span class="sh-file-thumb__icon">${fileIcon(22)}</span>`;
      return `
        <div class="sh-file-thumb">
          ${thumb}
          <span class="sh-file-thumb__name">${esc(a.filename)}</span>
          <button class="sh-file-thumb__remove" data-context="${context}" data-index="${i}">&times;</button>
        </div>`;
    }).join('');

    // Bind remove handlers
    $$('.sh-file-thumb__remove', container).forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const ctx = btn.dataset.context;
        const idx = parseInt(btn.dataset.index);
        const arr = ctx === 'reply' ? state.replyAttachments : state.createAttachments;
        if (arr[idx] && arr[idx].previewUrl) URL.revokeObjectURL(arr[idx].previewUrl);
        arr.splice(idx, 1);
        renderAttachmentPreviews(ctx);
        if (ctx === 'reply') updateSendButton('reply');
        if (ctx === 'create') updateSubmitButton();
      });
    });
  }

  function clearAttachmentPreviews(context) {
    const arr = context === 'reply' ? state.replyAttachments : state.createAttachments;
    arr.forEach(a => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
    arr.length = 0;
    renderAttachmentPreviews(context);
  }

  /* ══════════════════════════════════════════
     LIGHTBOX
  ══════════════════════════════════════════ */

  function openLightbox(url) {
    const lb = $('#sh-lightbox');
    const img = $('#sh-lightbox-img');
    if (!lb || !img) return;
    img.src = url;
    lb.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    const lb = $('#sh-lightbox');
    if (!lb) return;
    lb.style.display = 'none';
    document.body.style.overflow = '';
  }

  /* ══════════════════════════════════════════
     VIEW MANAGEMENT
  ══════════════════════════════════════════ */

  function showView(view) {
    // view: 'list', 'thread', 'create'
    const views = { list: '#sh-list-view', thread: '#sh-thread-view', create: '#sh-create-view' };
    Object.entries(views).forEach(([key, sel]) => {
      const el = $(sel);
      if (el) el.style.display = key === view ? '' : 'none';
    });
  }

  function updateSendButton() {
    const textEl = $('#sh-reply-text');
    const btn = $('#sh-reply-send-btn');
    if (!btn) return;
    const hasContent = (textEl && textEl.value.trim()) || state.replyAttachments.length > 0;
    btn.disabled = !hasContent;
  }

  function updateSubmitButton() {
    const subject = ($('#sh-subject') || {}).value || '';
    const message = ($('#sh-message') || {}).value || '';
    const btn = $('#sh-submit-ticket-btn');
    if (btn) btn.disabled = !(subject.trim() && message.trim());
  }

  /* ══════════════════════════════════════════
     EVENT BINDING
  ══════════════════════════════════════════ */

  function bindEvents() {
    // New ticket buttons
    const newBtn = $('#sh-new-ticket-btn');
    const emptyNewBtn = $('#sh-empty-new-btn');
    if (newBtn) newBtn.addEventListener('click', openCreateView);
    if (emptyNewBtn) emptyNewBtn.addEventListener('click', openCreateView);

    // Manual refresh button — lets users retry when the list looks wrong/stale
    const refreshBtn = $('#sh-refresh-btn');
    if (refreshBtn) refreshBtn.addEventListener('click', () => { loadTickets(); });

    // Back buttons — re-fetch tickets silently so the list reflects any backend changes
    // (newly created tickets, status updates that happened while reading a thread, etc.)
    const backBtn = $('#sh-back-btn');
    const createBackBtn = $('#sh-create-back-btn');
    const cancelBtn = $('#sh-cancel-ticket-btn');
    if (backBtn) backBtn.addEventListener('click', () => { showView('list'); loadTickets(1, false, true); });
    if (createBackBtn) createBackBtn.addEventListener('click', () => { showView('list'); loadTickets(1, false, true); });
    if (cancelBtn) cancelBtn.addEventListener('click', () => { showView('list'); loadTickets(1, false, true); });

    // Filters
    $$('.sh-filter').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('.sh-filter').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        state.currentFilter = btn.dataset.shFilter;
        loadTickets();
      });
    });

    // Search
    const searchInput = $('#sh-search-input');
    if (searchInput) {
      let debounce;
      searchInput.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
          state.searchQuery = searchInput.value.trim();
          renderTicketList();
        }, 300);
      });
    }

    // Load more
    const loadMoreBtn = $('#sh-load-more-btn');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => loadTickets(state.currentPage + 1, true));
    }

    // Retry
    const retryBtn = $('#sh-retry-btn');
    if (retryBtn) retryBtn.addEventListener('click', () => loadTickets());

    // Reply
    const replyText = $('#sh-reply-text');
    const replySendBtn = $('#sh-reply-send-btn');
    if (replyText) replyText.addEventListener('input', updateSendButton);
    if (replySendBtn) replySendBtn.addEventListener('click', sendReply);

    // Reply file attach
    const replyFileInput = $('#sh-reply-file');
    if (replyFileInput) {
      replyFileInput.addEventListener('change', () => {
        handleFileSelect(replyFileInput.files, 'reply');
        replyFileInput.value = '';
      });
    }

    // Create form validation
    const subjectInput = $('#sh-subject');
    const messageInput = $('#sh-message');
    if (subjectInput) subjectInput.addEventListener('input', updateSubmitButton);
    if (messageInput) messageInput.addEventListener('input', updateSubmitButton);

    // Create file attach
    const createFileInput = $('#sh-create-file');
    if (createFileInput) {
      createFileInput.addEventListener('change', () => {
        handleFileSelect(createFileInput.files, 'create');
        createFileInput.value = '';
      });
    }

    // Drag & drop on create dropzone
    const dropzone = $('#sh-dropzone-label');
    if (dropzone) {
      ['dragenter', 'dragover'].forEach(evt => {
        dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.classList.add('sh-dropzone--active'); });
      });
      ['dragleave', 'drop'].forEach(evt => {
        dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.classList.remove('sh-dropzone--active'); });
      });
      dropzone.addEventListener('drop', (e) => {
        if (e.dataTransfer.files.length) handleFileSelect(e.dataTransfer.files, 'create');
      });
    }

    // Submit ticket
    const submitBtn = $('#sh-submit-ticket-btn');
    if (submitBtn) submitBtn.addEventListener('click', submitTicket);

    // Lightbox close
    const lbClose = $('.sh-lightbox__close');
    const lbBackdrop = $('.sh-lightbox__backdrop');
    if (lbClose) lbClose.addEventListener('click', closeLightbox);
    if (lbBackdrop) lbBackdrop.addEventListener('click', closeLightbox);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeLightbox();
    });

    // Ctrl+Enter to send reply
    const replyTextarea = $('#sh-reply-text');
    if (replyTextarea) {
      replyTextarea.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') sendReply();
      });
    }
  }

  /* ══════════════════════════════════════════
     INIT
  ══════════════════════════════════════════ */

  function init() {
    bindEvents();
    loadTickets();

    // Check URL for deep link: #messages/ticket/123
    const hash = location.hash;
    const ticketMatch = hash.match(/messages\/ticket\/(\d+)$/);
    if (ticketMatch) {
      openThread(ticketMatch[1]);
    }
  }

  /* ── Public API ── */
  return {
    init,
    refresh: loadTickets,  // public re-fetch; called when a customer reopens the Support tab
    openThread,
    closeLightbox,
    // Expose for external entry points (order page "Need help?" button)
    createTicket: function(opts) {
      openCreateView();
      if (opts) {
        if (opts.category) { const el = $('#sh-category'); if (el) el.value = opts.category; }
        if (opts.order) { const el = $('#sh-order'); if (el) el.value = opts.order; }
        if (opts.subject) { const el = $('#sh-subject'); if (el) el.value = opts.subject; }
      }
      updateSubmitButton();
    },
  };

})();
